// Epoch 32: Contact Sales Modal for Enterprise Inquiries
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: string; // Which page triggered the modal
  buttonId?: string; // Specific button identifier
}

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({
  isOpen,
  onClose,
  source,
  buttonId = 'unknown'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    team_size: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const modalRef = useRef<HTMLDivElement>(null);
  const ga = useGoogleAnalytics();

  // Define handleClose first, before any useEffect that uses it
  const handleClose = useCallback(() => {
    if (!submitted && hasInteracted) {
      ga.trackFormAbandonment('contact_sales', Object.keys(formData).find(key => formData[key as keyof typeof formData]) || 'none');
    }
    onClose();
    // Reset form state
    setFormData({ name: '', email: '', company: '', team_size: '', message: '' });
    setError(null);
    setSubmitted(false);
  }, [submitted, hasInteracted, formData, ga, onClose]);

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      ga.trackModalOpen('contact_sales', buttonId);
      setHasInteracted(false); // Reset interaction tracking
    }
  }, [isOpen, buttonId, ga]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Track first interaction
    if (!hasInteracted) {
      ga.trackFormStart('contact_sales');
      setHasInteracted(true);
    }
    
    // Track field interaction
    ga.trackFieldInteraction(name, 'contact_sales');
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your name');
      ga.trackFormError('contact_sales', 'Name required');
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Please enter your email');
      ga.trackFormError('contact_sales', 'Email required');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      ga.trackFormError('contact_sales', 'Invalid email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Track submission attempt
    const hasOptionalFields = Boolean(formData.company || formData.team_size || formData.message);
    ga.trackFormSubmitAttempt('contact_sales', hasOptionalFields);
    
    try {
      // Get UTM parameters and GA IDs
      const utmParams = ga.getUTMParams();
      const gaClientId = ga.getGAClientId();
      const gaSessionId = ga.getGASessionId();
      
      // Submit to Edge Function
      const { error: submitError } = await supabase.functions.invoke('demo-request-handler', {
        body: {
          ...formData,
          request_type: 'contact_sales',
          source_page: source,
          source_button: buttonId,
          ...utmParams,
          ga_client_id: gaClientId,
          ga_session_id: gaSessionId
        }
      });
      
      if (submitError) {
        throw submitError;
      }
      
      // Track success
      ga.trackFormSubmitSuccess('contact_sales', formData.company, buttonId);
      
      // Show success state
      setSubmitted(true);
      
    } catch (err) {
      console.error('Error submitting sales inquiry:', err);
      setError('Something went wrong. Please try again or email us directly at jd@standingencore.com');
      ga.trackFormError('contact_sales', 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all"
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {!submitted ? (
            <div className="p-6">
              <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Contact Our Sales Team
                </h2>
                <p className="text-gray-600">
                  Get a custom enterprise quote tailored to your needs
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="John Smith"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="john@company.com"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Acme Corp"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="team_size" className="block text-sm font-medium text-gray-700 mb-1">
                    Team Size <span className="text-gray-400">(optional)</span>
                  </label>
                  <select
                    id="team_size"
                    name="team_size"
                    value={formData.team_size}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="">Select team size</option>
                    <option value="1-5">1-5</option>
                    <option value="6-20">6-20</option>
                    <option value="21-50">21-50</option>
                    <option value="51-100">51-100</option>
                    <option value="100+">100+</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Tell us about your needs <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Looking for enterprise features, custom integrations, dedicated support..."
                    disabled={loading}
                  />
                </div>
                
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                      loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                  >
                    {loading ? 'Submitting...' : 'Contact Sales'}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              
              <p className="mt-4 text-xs text-center text-gray-500">
                By submitting, you agree to our Privacy Policy and Terms of Service
              </p>
            </div>
          ) : (
            <div className="p-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You for Your Interest!
                </h3>
                <p className="text-gray-600 mb-6">
                  Our sales team will contact you within 24 hours to discuss your enterprise needs.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-700 mb-3">
                    In the meantime, you can:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 text-left">
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Schedule a demo at your convenience</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Email us directly at jd@standingencore.com</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Explore our features at www.standingencore.com</span>
                    </li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <a
                    href="https://calendly.com/aipenguins/standing-encore"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => ga.trackCalendlyView('contact_sales')}
                    className="flex-1 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Schedule Demo Now
                  </a>
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactSalesModal;